import React from 'react'

import { DevTool } from '@hookform/devtools'
import { Menu } from '@mui/icons-material'
import { LoadingButton, LoadingButton as MLoadingButton } from '@mui/lab'
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Link,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import Decimal from 'decimal.js'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { ControlledSelector, ControlledTextField, TextInput } from 'components/common'
import XeroPurchaseCodesMultipleAutocomplete from 'components/common/custom-queries/XeroCodesMultipleAutocomplete'
import {
  EmailSentStatus,
  EmailTemplateType,
  useCreateInvoiceTaskMutation,
  useDeleteInstallationByIdMutation,
  useProcessInvoiceTaskMutation,
  useRefreshExchangeRatesMutation,
  useSendTestNotificationMutation,
  useTestSendingEmailToCustomerMutation,
  useTestSendingEmailToDriverMutation,
} from 'generated/graphql'

import { ButtonChangeTheme } from '../navigation'

type EmailFormData = {
  type: EmailTemplateType
  bookingId: number
  driverId: number
  bookingAddressId?: number
}

type MobileNotificationFormData = {
  driverId: number
  title: string
  subtitle: string
  message: string
  accessToken?: string
}
export default function Artifacts() {
  const [loading, setLoading] = React.useState(false)

  const handleLoadingClick = () => {
    setLoading(true)
    setTimeout(() => setLoading(false), 3000)
  }

  // Testing Toasts
  const types = ['info', 'success', 'warning', 'error', 'default', 'dark']
  let iterator = 0
  const handleToastButton = () => {
    const type = types[iterator % types.length] as any
    toast(type, { type, autoClose: 10000 })
    iterator++
  }
  const handleToastAllButton = () => {
    types.forEach((type: any) => {
      toast(type, { type, autoClose: 10000 })
    })
  }

  const { control, watch, trigger } = useForm({
    shouldUnregister: true,
    defaultValues: {
      x: 0.3,
      y: 0.1,
    },
  })

  const x = Number(watch('x', 0))
  const y = Number(watch('y', 0))

  const decimalX = new Decimal(x)
  const decimalY = new Decimal(y)

  // https://mikemcl.github.io/decimal.js/#prototype-methods

  if (x === 0 && y === 0) {
    trigger()
  }

  const decimalJs = decimalX.minus(decimalY)
  const decimalNative = x - y

  const myAppBar = (appBarColor, titleColor = 'initial') => (
    <AppBar position='static' color={appBarColor}>
      <Toolbar>
        <IconButton
          edge='start'
          sx={{ marginRight: 2 }}
          color='inherit'
          aria-label='menu'
          size='large'>
          <Menu />
        </IconButton>
        <Typography variant='h6' sx={{ flexGrow: 1 }} color={titleColor as any}>
          OnePoint
        </Typography>
        <Typography sx={{ flexGrow: 1 }}>{`${appBarColor}${
          titleColor ? ` with ${titleColor} title` : ''
        }`}</Typography>
        <Button color='inherit'>Login</Button>
      </Toolbar>
    </AppBar>
  )

  // Testing Emails
  const { control: control_email, getValues } = useForm<EmailFormData>({
    shouldUnregister: true,
    defaultValues: {
      type: EmailTemplateType.CustomerBooking,
    },
  })

  // Testing Mobile push notifications
  const { control: controlMobile, getValues: getValuesMobile } =
    useForm<MobileNotificationFormData>({
      shouldUnregister: true,
      defaultValues: {
        accessToken: '',
        driverId: 0,
        message: '',
        subtitle: '',
        title: '',
      },
    })

  // testing exchange rates
  const [refreshExchangeRates, { loading: refreshingExchangeRates }] =
    useRefreshExchangeRatesMutation({
      onCompleted: () => {
        toast.success('Exchange rates are refreshed')
      },
    })

  const [sendEmailToCustomer, { loading: sendingEmailToCustomer }] =
    useTestSendingEmailToCustomerMutation({
      onCompleted: (response) => {
        if (!response) {
          toast.error('Something went wrong')
          return
        }

        if (response.testSendingEmailToCustomer === EmailSentStatus.Success) {
          toast.success(`Email to Customer sent successfully.`)
        } else {
          toast.warn('Email to Customer not sent, error: ' + response.testSendingEmailToCustomer)
        }
      },
    })

  const [sendEmailToDriver, { loading: sendingEmailToDriver }] =
    useTestSendingEmailToDriverMutation({
      onCompleted: (response) => {
        if (!response) {
          toast.error('Something went wrong')
          return
        }

        if (response.testSendingEmailToDriver === EmailSentStatus.Success) {
          toast.success(`Email to Driver sent successfully.`)
        } else {
          toast.warn('Email to Driver not sent, error: ' + response.testSendingEmailToDriver)
        }
      },
    })

  const [sendNotification, { loading: sendingNotificaton }] = useSendTestNotificationMutation({
    onCompleted: (response) => {
      console.log(response)
    },
  })

  const [createTask, { loading: creatingTask }] = useCreateInvoiceTaskMutation({
    onCompleted: (response) => {
      if (response.createInvoiceTask) {
        toast.success('task was created')
      } else {
        toast.warning('task exists')
      }
    },
  })

  const [updateTask, { loading: updatingTask }] = useProcessInvoiceTaskMutation({
    onCompleted: (response) => {
      if (response.processTasks) {
        toast.success('invoices was processed')
      } else {
        toast.error('something went wrong')
      }
    },
  })

  const handleSendToCustomer = () => {
    const values = getValues()
    sendEmailToCustomer({
      variables: {
        type: values.type,
        bookingId: +values.bookingId,
        bookingAddressId: Number(values.bookingAddressId),
      },
    })
  }

  const handleSendNotification = () => {
    const values = getValuesMobile()

    sendNotification({
      variables: {
        ...values,
        driverId: Number(values.driverId),
      },
    })
  }

  const handleSendToDriver = () => {
    const values = getValues()
    sendEmailToDriver({
      variables: {
        type: values.type,
        bookingId: +values.bookingId,
        driverId: +values.driverId,
      },
    })
  }

  /* Test Azure Notification Hub */
  // const { data: allNotificationHubRegistration } = useAllNotificationHubRegistrationQuery()

  const { control: controlANH, handleSubmit: handleInstallationSubmit } = useForm({
    defaultValues: {
      installationId: '',
    },
  })

  const [deleteById] = useDeleteInstallationByIdMutation()

  const deviceInstallationDelete = (data: any) => {
    deleteById({
      variables: {
        installationId: Number(data.installationId),
      },
    })
  }

  return (
    <>
      <Container>
        <Typography variant='h5'>Test Sending Emails</Typography>
        <Box>
          <form>
            <Grid container spacing={3}>
              <Grid item>
                <ControlledSelector
                  control={control_email}
                  label='Type'
                  name='type'
                  options={Object.values(EmailTemplateType).map((type) => ({
                    value: type,
                    label: type,
                  }))}
                />
              </Grid>
              <Grid item>
                <ControlledTextField
                  name='bookingId'
                  control={control_email}
                  label='Booking ID'
                  type='number'
                />
              </Grid>
              <Grid item>
                <ControlledTextField
                  name='bookingAddressId'
                  label='Booking address ID'
                  type='number'
                  control={control_email}
                />
              </Grid>
              <Grid item>
                <ControlledTextField
                  name='driverId'
                  control={control_email}
                  label='Driver ID'
                  type='number'
                />
              </Grid>
              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={sendingEmailToCustomer}
                  onClick={handleSendToCustomer}>
                  Send to CUSTOMER
                </LoadingButton>
              </Grid>
              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={sendingEmailToDriver}
                  onClick={handleSendToDriver}>
                  Send to DRIVER
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </Box>

        <Typography variant='h5'>Buttons</Typography>

        <Box
          sx={{
            '& > *': {
              margin: 1,
            },
          }}>
          <Button variant='contained'>Default</Button>
          <Button variant='contained' color='primary'>
            Primary
          </Button>
          <Button variant='contained' color='secondary'>
            Secondary
          </Button>
          <Button variant='contained' disabled>
            Disabled
          </Button>
          <Button variant='contained' color='primary' href='#contained-buttons'>
            Link
          </Button>
          <LoadingButton
            variant='contained'
            onClick={handleLoadingClick}
            loading={loading}
            style={{ minWidth: 120 }}>
            Loading Button
          </LoadingButton>

          <Button variant='contained' onClick={handleToastButton}>
            One Toast
          </Button>
          <Button variant='contained' onClick={handleToastAllButton}>
            All Toasts
          </Button>

          <ButtonChangeTheme />
        </Box>

        <Typography variant='h6'>Alerts (and colors)</Typography>
        <Box>
          <MuiAlert elevation={6} variant='filled' severity='error'>
            This is an error message!
          </MuiAlert>
          <MuiAlert elevation={6} variant='filled' severity='warning'>
            This is a warning message!
          </MuiAlert>
          <MuiAlert elevation={6} variant='filled' severity='info'>
            This is an information message!
          </MuiAlert>
          <MuiAlert elevation={6} variant='filled' severity='success'>
            This is a success message!
          </MuiAlert>
        </Box>

        <Box margin={10}>
          <Typography variant='h6'>Test Mobile app notifications</Typography>
          <Paper>
            <form>
              <Grid container spacing={3}>
                {/* Title */}
                <Grid item>
                  <ControlledTextField
                    name='title'
                    control={controlMobile}
                    label='Title'
                    required
                  />
                </Grid>

                {/* Subtitle */}
                <Grid item>
                  <ControlledTextField
                    name='subtitle'
                    control={controlMobile}
                    label='Subtitle'
                    required
                  />
                </Grid>

                {/* message */}
                <Grid item>
                  <ControlledTextField
                    name='message'
                    control={controlMobile}
                    label='Message'
                    required
                  />
                </Grid>

                {/* driver id */}
                <Grid item>
                  <ControlledTextField
                    name='driverId'
                    type='number'
                    control={controlMobile}
                    label='Driver Id'
                    required
                  />
                </Grid>

                {/* access token */}
                <Grid item>
                  <ControlledTextField
                    name='accessToken'
                    control={controlMobile}
                    label='Access token'
                  />
                </Grid>
                <Grid item>
                  <LoadingButton
                    variant='contained'
                    color='primary'
                    loading={sendingNotificaton}
                    onClick={handleSendNotification}>
                    Send to DRIVER APP
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Box margin={10}>
          <Paper>
            <form>
              <Grid container spacing={3}>
                <Grid item>
                  <ControlledTextField
                    name='installationId'
                    type='string'
                    control={controlANH}
                    label='Installation Id'
                    required
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant='contained'
                    onClick={handleInstallationSubmit(deviceInstallationDelete)}>
                    DELETE DEVICE INSTALLATION BY ID
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Box margin={10}>
          <Typography variant='h6'>
            <Link href='https://mikemcl.github.io/decimal.js/#prototype-methods'>Decimal JS</Link>
          </Typography>
          <Paper>
            <form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ControlledTextField name='x' control={control} label='x' type='number' />
                </Grid>
                <Grid item xs={12}>
                  <ControlledTextField name='y' control={control} label='y' type='number' />
                </Grid>
                <Grid item>
                  <TextField
                    variant='outlined'
                    value={decimalJs}
                    label='Decimal.js x-y'
                    type='number'
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    variant='outlined'
                    value={decimalNative}
                    label='Native x-y='
                    type='number'
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        {/* <Typography variant='h6'>Datepicker</Typography>
        <Box sx={{ margin: 1 }}>
          <DatePicker
            label='With keyboard'
            inputVariant='outlined'
            value={selectedDate}
            onChange={(date) => handleDateChange(date)}
          />
        </Box> */}

        <Typography variant='h6'>Text Field</Typography>
        <Box sx={{ margin: 1 }}>
          <TextInput label='Read Only' name='readOnly' value='Test text' readOnly />
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3 }}>
          Exchange Rates
        </Typography>
        <Box>
          <MLoadingButton onClick={() => refreshExchangeRates()} loading={refreshingExchangeRates}>
            Refresh Exchange Rates
          </MLoadingButton>
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3 }}>
          Scheduled Invoice Service
        </Typography>
        <Box>
          <MLoadingButton onClick={() => createTask()} loading={creatingTask}>
            Create task
          </MLoadingButton>
          <MLoadingButton onClick={() => updateTask()} loading={updatingTask}>
            Process task
          </MLoadingButton>
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3 }}>
          App Bars
        </Typography>
        <Box
          sx={{
            '& > *': {
              marginTop: 2,
            },
          }}>
          {myAppBar('default')}
          {myAppBar('primary')}
          {myAppBar('secondary')}
          {myAppBar('transparent')}
          <Typography variant='h6'>&nbsp;</Typography>
          {myAppBar('default', 'primary')}
          {myAppBar('secondary', 'primary')}
          {myAppBar('transparent', 'primary')}
          <Typography variant='h6'>&nbsp;</Typography>
          {myAppBar('default', 'secondary')}
          {myAppBar('primary', 'secondary')}
          {myAppBar('transparent', 'secondary')}
        </Box>
        <Box sx={{ mt: 2 }}>
          <XeroPurchaseCodesMultipleAutocomplete control={control} />
        </Box>
      </Container>

      <DevTool control={control} />
    </>
  )
}
